import { inject, Pipe, PipeTransform } from '@angular/core';
import {
  WcgDateOnlyPipe,
  WcgDateTimePipe,
  WcgTimeOnlyPipe,
} from '@wcg/frontend-framework';
import { appendUTCOffset } from '../utils/date-offset-calculator.util';

const MS_PER_DAY = 86_400_000;

/** Calendar days back that get a relative label; older values show the full date. */
const RELATIVE_DAY_LABELS = ['Today', 'Yesterday', '2 days ago'];

function startOfDay(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
}

/**
 * Whole calendar days between two instants, in local time. Rounding absorbs the
 * 23/25-hour days a DST change produces.
 */
function calendarDaysBetween(from: Date, to: Date): number {
  return Math.round(
    (startOfDay(to).getTime() - startOfDay(from).getTime()) / MS_PER_DAY,
  );
}

/** `null` when the date is in the future or older than the labelled window. */
function relativeDayLabel(daysAgo: number): string | null {
  if (daysAgo < 0) {
    return null;
  }
  return RELATIVE_DAY_LABELS[daysAgo] ?? null;
}

@Pipe({
  name: 'ermDate',
})
export class ERMDatePipe implements PipeTransform {
  private readonly _datePipe = inject(WcgDateOnlyPipe);
  transform(value: string | Date | null | undefined): string {
    if (!value || value === 'None') {
      return 'N/A';
    }
    return this._datePipe.transform(appendUTCOffset(`${value}`));
  }
}

@Pipe({
  name: 'ermDateTime',
})
export class ERMDateTimePipe implements PipeTransform {
  private readonly _dateTimePipe = inject(WcgDateTimePipe);
  transform(value: string | Date | null | undefined): string {
    if (!value || value === 'None') {
      return 'N/A';
    }
    return this._dateTimePipe.transform(appendUTCOffset(`${value}`));
  }
}

@Pipe({
  name: 'ermDateTimeUpdated',
})
export class ERMDateTimeUpdatedPipe implements PipeTransform {
  private readonly _dateTimePipe = inject(WcgDateTimePipe);
  private readonly _timeOnlyPipe = inject(WcgTimeOnlyPipe);

  transform(value: string | Date | null | undefined): string {
    if (!value || value === 'None') {
      return 'N/A';
    }
    const offsetValue = appendUTCOffset(`${value}`);
    const dateValue = new Date(offsetValue);
    if (isNaN(dateValue.getTime())) {
      return 'N/A';
    }
    const label = relativeDayLabel(calendarDaysBetween(dateValue, new Date()));
    if (!label) {
      return this._dateTimePipe.transform(offsetValue);
    }
    // Inside the labelled window the date is implied, so only the time is shown.
    return `${label} ${this._timeOnlyPipe.transform(offsetValue)}`;
  }
}

@Pipe({
  name: 'ermTime',
})
export class ERMTimePipe implements PipeTransform {
  private readonly _dateTimePipe = inject(WcgDateTimePipe);
  transform(value: string | Date | null | undefined): string {
    if (!value || value === 'None') {
      return 'N/A';
    }
    return this._dateTimePipe.transform(appendUTCOffset(`${value}`));
  }
}
